import React, { useCallback, useEffect, useState } from "react";
import axios from "../../API/axios";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CandidateReadinessBadges from "../../components/CandidateReadinessBadges";
import { Portal } from "../../components/Portal";
import "../../styles/recruiterFeatures.css";

/**
 * Recommended candidates for a job — company JobApplications / Manage Jobs UX.
 * No gate-status filter (crossed out in product). Min score + search + assign via OfferJob.
 */
const RecommendedCandidatesModal = ({ show, jobId, jobTitle, onClose }) => {
  const { t } = useTranslation();
  const sessionId = localStorage.getItem("sessionId");
  const [search, setSearch] = useState("");
  const [minScore, setMinScore] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [title, setTitle] = useState(jobTitle || "");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [readinessFilter, setReadinessFilter] = useState("all");

  const load = useCallback(
    async (pageNumber = 1, refreshRanking = false) => {
      if (!jobId) return;
      setLoading(true);
      setError("");
      setMessage("");
      try {
        const res = await axios.post("JobApplication/RecommendedCandidates", {
          deviceType: "Web",
          sessionId,
          jobId,
          search: search || null,
          minScore: Number(minScore) || 0,
          pageNumber,
          pageSize,
          refreshRanking,
          readinessFilter: readinessFilter === "all" ? null : readinessFilter,
        });
        const data = res?.data?.responseData || {};
        setTitle(data.jobTitle || data.JobTitle || jobTitle || "");
        setCandidates(data.candidates || data.Candidates || []);
        setPage(data.pageNumber || data.PageNumber || pageNumber);
        setTotalPages(data.totalPages || data.TotalPages || 0);
        setMessage(res?.data?.message || "");
        setSelected({});
      } catch (e) {
        setError(e?.response?.data?.message || t("Recruiter.LoadFailed"));
        setCandidates([]);
      } finally {
        setLoading(false);
      }
    },
    [jobId, jobTitle, minScore, pageSize, search, sessionId, readinessFilter, t]
  );

  useEffect(() => {
    if (show && jobId) {
      load(1, false);
    }
  }, [show, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedUids = Object.keys(selected).filter((k) => selected[k]);

  const toggle = (uid) => setSelected((prev) => ({ ...prev, [uid]: !prev[uid] }));

  const assignSelected = async () => {
    if (!selectedUids.length) return;
    setAssigning(true);
    setError("");
    let ok = 0;
    let fail = 0;
    for (const userUid of selectedUids) {
      try {
        await axios.post("JobAction/OfferJob", {
          deviceType: "Web",
          sessionId,
          jobId,
          userUid,
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setAssigning(false);
    setMessage(
      t("Recruiter.AssignedResult", {
        ok,
        fail: fail ? t("Recruiter.AssignedFail", { fail }) : "",
      })
    );
    load(page, true);
  };

  if (!show) return null;

  return (
    <Portal layer="modal">
      <div className="modal fade show postAdd wlj-overlay-modal" style={{ display: "flex" }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content d-flex flex-column">
            <div className="setting-header d-flex justify-content-between align-items-center">
              <h6>{t("Recruiter.RecommendedCandidates")}</h6>
              <CloseSharpIcon className="material-symbols-sharp me-2 pointer" onClick={onClose} />
            </div>

            <div className="modal-body wlj-modal-body">
              {title ? <p className="fw-semibold mb-3">{title}</p> : null}

              <div className="fw-semibold mb-2">{t("Recruiter.CandidateSearchTitle")}</div>
              <div className="row g-2 align-items-end">
                <div className="col-12 col-md-4">
                  <label className="form-label small text-muted mb-1">{t("SearchJob.Search")}</label>
                  <input
                    className="form-control"
                    placeholder={t("Recruiter.CandidateSearch")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") load(1, false);
                    }}
                  />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small text-muted mb-1">{t("Recruiter.MinScore")}</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="form-control"
                    value={minScore}
                    onChange={(e) => setMinScore(e.target.value)}
                  />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small text-muted mb-1">{t("Recruiter.PageSize")}</label>
                  <select
                    className="form-select"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                  >
                    {[10, 20, 50].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label small text-muted mb-1">{t("Recruiter.Readiness")}</label>
                  <select
                    className="form-select"
                    value={readinessFilter}
                    onChange={(e) => setReadinessFilter(e.target.value)}
                  >
                    <option value="all">{t("Recruiter.AllCandidates")}</option>
                    <option value="kyc">{t("Recruiter.KycDone")}</option>
                    <option value="unverified">{t("Recruiter.KycPending")}</option>
                    <option value="dueSoon">{t("Recruiter.UpdateDueSoon")}</option>
                    <option value="overdue">{t("Recruiter.OneYearOverdue")}</option>
                    <option value="incomplete">{t("Recruiter.IncompleteProfile")}</option>
                  </select>
                </div>
              </div>

              <div className="d-flex flex-nowrap align-items-center justify-content-end gap-2 mt-2 mb-3">
                <button
                  type="button"
                  className="wljDangerBtn"
                  disabled={loading}
                  onClick={() => {
                    setSearch("");
                    setMinScore(0);
                    setPageSize(20);
                    setReadinessFilter("all");
                    setTimeout(() => load(1, false), 0);
                  }}
                >
                  {t("Recruiter.Reset")}
                </button>
                <button type="button" className="mainBtn1" onClick={() => load(1, false)} disabled={loading}>
                  {t("Recruiter.Apply")}
                </button>
              </div>

              <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                <button
                  type="button"
                  className="wljSecondaryBtn"
                  disabled={!selectedUids.length || assigning}
                  onClick={assignSelected}
                >
                  {t("Recruiter.AssignSelected", { count: selectedUids.length })}
                </button>
                <button
                  type="button"
                  className="wlj-btn-ghost"
                  disabled={loading}
                  onClick={() => load(page, true)}
                >
                  {t("Recruiter.RefreshRanking")}
                </button>
              </div>

              {message && !error && (
                <p className="small text-muted mb-2" role="status">
                  {message}
                </p>
              )}
              {loading && <p className="mb-2">{t("Loading")}</p>}
              {error && <p className="text-danger mb-2">{error}</p>}
              {!loading && !candidates.length && (
                <p className="text-muted mb-0">{message || t("Recruiter.NoCandidates")}</p>
              )}

              {candidates.map((c) => {
                const uid = c.userUid || c.UserUid;
                const score = c.matchScore ?? c.MatchScore ?? 0;
                const alreadyApplied = c.alreadyApplied || c.AlreadyApplied;
                return (
                  <div key={uid} className="d-flex align-items-start gap-2 py-3 border-bottom">
                    <input
                      type="checkbox"
                      className="mt-1 flex-shrink-0"
                      checked={!!selected[uid]}
                      onChange={() => toggle(uid)}
                      disabled={alreadyApplied}
                      aria-label={c.fullName || c.FullName || uid}
                    />
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="min-w-0">
                          <Link to={`/Profile/${uid}`} className="fw-semibold d-inline-block">
                            #{c.rank ?? c.Rank} {c.fullName || c.FullName}
                          </Link>
                          <div className="small text-muted text-break">
                            {c.headline || c.Headline || uid}
                          </div>
                          {(c.explain || c.Explain) && (
                            <div className="small mt-1">{c.explain || c.Explain}</div>
                          )}
                        </div>
                        <span className="wlj-status-chip wlj-status-chip--match flex-shrink-0">
                          {Math.round(score)}% {t("Recruiter.Match")}
                        </span>
                      </div>
                      <CandidateReadinessBadges candidate={c}>
                        {(c.rankingFrozen || c.RankingFrozen) && (
                          <span className="wlj-status-chip wlj-status-chip--muted">
                            {t("Recruiter.RankingFrozen")}
                          </span>
                        )}
                        {alreadyApplied && (
                          <span className="wlj-status-chip wlj-status-chip--info">
                            {t("Recruiter.AlreadyApplied")}
                          </span>
                        )}
                      </CandidateReadinessBadges>
                    </div>
                  </div>
                );
              })}

              <div className="d-flex justify-content-between align-items-center gap-2 pt-3 mt-1">
                <button
                  type="button"
                  className="wljSecondaryBtn"
                  disabled={page <= 1 || loading}
                  onClick={() => load(page - 1, false)}
                >
                  {t("Newsfeed.Previous")}
                </button>
                <span className="small text-muted text-center">
                  {t("Recruiter.PageOf", {
                    page: totalPages === 0 ? 0 : page,
                    total: totalPages,
                  })}
                </span>
                <button
                  type="button"
                  className="wljSecondaryBtn"
                  disabled={page >= totalPages || loading}
                  onClick={() => load(page + 1, false)}
                >
                  {t("Newsfeed.Next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default RecommendedCandidatesModal;
